import { useEffect, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { getPostWords } from '@/api/appstore.api';
import type { AppStore } from '@/interfaces/service/appstore/appstore.interface';
import { useAppStoreStore } from '@/stores/appstore.store';
import FlexBox from '@/components/FlexBox';
import LogoIcon from '@/components/svgs/LogoIcon';
import DashboardWatingDataFallback from '@/views/appstore/dashboard/components/DashboardWatingDataFallback';
import PostWordGraph from '@/views/appstore/dashboard/components/basecontents/PostWordGraph';
import theme from '@/app/theme';

const SummaryContainer = ({ isMobile, children }: { isMobile: boolean; children: JSX.Element }) => {
  return (
    <Stack
      sx={{
        width: '100%',
        border: `1px solid ${theme.palette.gray[100]}`,
        backgroundColor: theme.palette.gray[50],
        padding: isMobile ? '20px' : '24px',
        borderRadius: '8px'
      }}
      gap="16px"
    >
      <FlexBox style={{ justifyContent: 'start', gap: '8px' }}>
        <LogoIcon />
        <Typography variant="subtitle1" fontWeight={600}>
          AI 키워드 분석 결과
        </Typography>
      </FlexBox>
      {children}
    </Stack>
  );
};

const WordParagraph = ({ words }: { words: { word: string; count: number }[] | undefined }) => {
  if (!words) {
    return '';
  }

  return words.length > 0
    ? words.map(({ word }, i) => {
        const isLastWord = i === words.length - 1;
        return (
          <strong key={word} style={isLastWord ? {} : { marginRight: '4px' }}>
            {isLastWord ? word : `${word},`}
          </strong>
        );
      })
    : '';
};

const PostsWordsSummaries = ({ currentAppStore, isMobile }: { currentAppStore: AppStore; isMobile: boolean }) => {
  const { updatePostStatisitcs } = useAppStoreStore((state) => state);

  const [positiveWords, setPositiveWords] = useState<{ word: string; count: number }[]>([]);
  const [negativeWords, setNegativeWords] = useState<{ word: string; count: number }[]>([]);

  useEffect(() => {
    if (currentAppStore.status !== 'STABLE' || (positiveWords.length > 0 && negativeWords.length === 0)) {
      return;
    }
    const getWordData = async () => {
      const res = await getPostWords({
        storeId: currentAppStore.storeId,
        channelIdList: currentAppStore.channels.map((channel) => channel.channelId)
      });
      if (res) {
        const { POSITIVE = {}, NEGATIVE = {} } = res;
        setPositiveWords(
          Object.entries(POSITIVE).map(([key, value]) => {
            return { word: key, count: Number(value) };
          }) ?? []
        );
        setNegativeWords(
          Object.entries(NEGATIVE).map(([key, value]) => {
            return { word: key, count: Number(value) };
          }) ?? []
        );
        updatePostStatisitcs(currentAppStore.storeId, 'words', {
          POSITIVE,
          NEGATIVE
        });
      }
    };
    getWordData();
  }, [currentAppStore]);

  if (currentAppStore.status !== 'STABLE' || !(positiveWords.length > 0 || negativeWords.length > 0)) {
    return (
      <SummaryContainer isMobile={isMobile}>
        <DashboardWatingDataFallback message="AI 키워드를 도출하는 중입니다." />
      </SummaryContainer>
    );
  } else if (positiveWords.length > 0 || negativeWords.length > 0) {
    return (
      <Stack
        sx={{
          width: '100%'
        }}
        gap="16px"
      >
        <SummaryContainer isMobile={isMobile}>
          <Box>
            <Typography variant="subtitle2">
              우리 병원에 대해 환자들이 자주 언급한 긍정 키워드와 부정 키워드입니다. <br /> 키워드를 클릭하면 해당 키워드가 포함된 리뷰를 확인 할 수 있습니다.
            </Typography>
          </Box>
        </SummaryContainer>
        <PostWordGraph appStoreId={currentAppStore.storeId} wordData={{ POSITIVE: positiveWords, NEGATIVE: negativeWords }} />
      </Stack>
    );
  } else {
    return '';
  }
};

export default PostsWordsSummaries;
